import {
  DependencyList,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

const CLIPPING_OVERFLOWS = new Set([
  "auto",
  "clip",
  "hidden",
  "overlay",
  "scroll",
]);

/**
 * Viewport y-coordinate of the lowest point an element can occupy, plus the
 * nearest ancestor that clips overflow.
 *
 * Only style values are inputs: every ancestor's bottom padding and border is
 * space reserved below the element, and the sum comes off the viewport height.
 * An ancestor's rect is never an input, because a scroll region sized by its
 * content has a bottom that follows the element inside it.
 *
 * `boundary` is returned only as something to watch for resizes. Its size
 * reaches no part of the math, so observing it cannot oscillate.
 */
export function measureAvailableBottom(element: HTMLElement): {
  bottom: number;
  boundary: HTMLElement | null;
} {
  let reserved = 0;
  let boundary: HTMLElement | null = null;

  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const style = getComputedStyle(ancestor);

    reserved +=
      (parseFloat(style.paddingBottom) || 0) +
      (parseFloat(style.borderBottomWidth) || 0);

    if (!boundary && CLIPPING_OVERFLOWS.has(style.overflowY)) {
      boundary = ancestor;
    }
  }

  return { bottom: window.innerHeight - reserved, boundary };
}

export function availableBottom(element: HTMLElement): number {
  return measureAvailableBottom(element).bottom;
}

export function useMaxAvailableHeight({
  areaRef,
  contentBelowRef,
  deps,
}: {
  areaRef: React.RefObject<HTMLDivElement>;
  contentBelowRef: React.RefObject<HTMLDivElement> | undefined;
  deps?: DependencyList;
}) {
  const [availableHeight, setAvailableHeight] = useState(0);
  const [prevContentHeight, setPrevContentHeight] = useState(0);
  const [boundary, setBoundary] = useState<HTMLElement | null>(null);

  const calculateAvailableHeight = useCallback(() => {
    const area = areaRef.current;
    const distFromTop = area?.getBoundingClientRect().top || 0;
    const contentBelowHeight = contentBelowRef?.current?.offsetHeight || 0;
    const measured = area
      ? measureAvailableBottom(area)
      : { bottom: window.innerHeight, boundary: null };
    const newAvailableHeight =
      measured.bottom - distFromTop - contentBelowHeight;

    setAvailableHeight(newAvailableHeight);
    setPrevContentHeight(contentBelowHeight);
    setBoundary(measured.boundary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaRef, contentBelowRef, deps]);

  useEffect(() => {
    calculateAvailableHeight();

    const handleResize = () => {
      calculateAvailableHeight();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [calculateAvailableHeight]);

  // Recalculate when the footer element's size changes (e.g. it appears after
  // the virtual table has already been sized, due to memoization of the parent).
  useEffect(() => {
    const el = contentBelowRef?.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calculateAvailableHeight();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [calculateAvailableHeight, contentBelowRef]);

  // The page keeps mounting after first paint (filters, banners), moving the
  // area without resizing it or the footer. Observation target only, so safe.
  useEffect(() => {
    if (!boundary) {
      return;
    }

    const observer = new ResizeObserver(() => {
      calculateAvailableHeight();
    });
    observer.observe(boundary);
    return () => observer.disconnect();
  }, [boundary, calculateAvailableHeight]);

  useLayoutEffect(() => {
    const contentBelowHeight = contentBelowRef?.current?.offsetHeight || 0;

    if (contentBelowHeight !== prevContentHeight) {
      calculateAvailableHeight();
    }
  });

  return availableHeight;
}
